import React, { useState, useEffect } from 'react';
import { Box, Typography, Link as MuiLink, List, ListItem, CircularProgress, Paper, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// API endpoint for our Vercel serverless function proxy
const PROXY_API_ENDPOINT = '/api/rss-proxy';

const NewsFeed = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Fetch data using the Vercel serverless function proxy
        const fetchFeed = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(PROXY_API_ENDPOINT);
                
                if (!response.ok) {
                    // Try to get error message from proxy response if possible
                    let errorMsg = `HTTP error! status: ${response.status}`;
                    try {
                       const errData = await response.json();
                       errorMsg = errData.error || errorMsg;
                    } catch {}
                    throw new Error(errorMsg);
                }
                
                const text = await response.text();
                
                // Parse the XML feed
                const parser = new DOMParser();
                const xml = parser.parseFromString(text, "application/xml");
                
                // Check for parser errors (important for XML)
                const parserError = xml.querySelector("parsererror");
                if (parserError) {
                    console.error("XML Parsing Error:", parserError.textContent);
                    throw new Error("Failed to parse RSS feed XML.");
                }

                const feedItems = xml.querySelectorAll("item");
                if (feedItems.length === 0 && !text.includes('<item>')) {
                     // Check if the response seems empty or not like an RSS feed
                     console.warn("Received empty or non-RSS response from proxy:", text.substring(0, 500));
                     // Consider setting items to empty array or specific message
                }
                
                const parsedItems = Array.from(feedItems).slice(0, 10).map(item => ({ // Limit to 10 items
                    title: item.querySelector("title")?.textContent || 'No Title',
                    link: item.querySelector("link")?.textContent || null,
                    pubDate: item.querySelector("pubDate")?.textContent ? new Date(item.querySelector("pubDate").textContent).toLocaleDateString() : 'No Date',
                }));
                
                // Filter out items without a valid link
                setItems(parsedItems.filter(item => item.link && item.link !== '#'));
            } catch (e) {
                console.error("Failed to fetch or parse RSS feed via proxy:", e);
                setError(`Failed to load news feed: ${e.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchFeed();
    }, []);

    return (
        <Paper elevation={3} sx={{ p: 2, height: '100%', bgcolor: 'rgba(20,20,20,0.8)', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.light' }}>
                Federal Workforce News
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>}
            {error && <Typography color="error" sx={{ my: 2 }}>{error}</Typography>}
            {!loading && !error && items.length === 0 && (
                 <Typography color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
                    No news items available at the moment.
                </Typography>
            )}
            {!loading && !error && items.length > 0 && (
                <List disablePadding>
                    {items.map((item, index) => (
                        <ListItem key={item.link || index} disablePadding sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                            <Box>
                                <Typography 
                                    variant="caption" 
                                    display="block"
                                    sx={{ 
                                        mb: 0.5,
                                        color: 'text.secondary', 
                                        fontFamily: '"Roboto Condensed", sans-serif',
                                        fontSize: '0.7rem',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {item.pubDate}
                                </Typography>
                                <MuiLink 
                                    component={RouterLink}
                                    to={`/news/${encodeURIComponent(item.link)}`}
                                    variant="body1"
                                    sx={{ 
                                        display: 'block', 
                                        fontWeight: 'medium', 
                                        color: 'inherit', 
                                        textDecoration: 'none', 
                                        '&:hover': { textDecoration: 'underline'} 
                                    }}
                                >
                                    {item.title}
                                </MuiLink>
                            </Box>
                        </ListItem>
                    ))}
                </List>
            )}
        </Paper>
    );
};

export default NewsFeed; 